import { memo, useEffect } from 'react';
import { useToggle } from 'react-use';

import { AlertBanner } from '@air/primitive-alert-banner';

import { usePendingEmailSurface } from '~/hooks/usePendingEmailSurface';

export const PendingEmailBanner = memo(() => {
  const [isDismissed, toggleIsDismissed] = useToggle(false);
  const {
    cancel,
    isCanceling,
    isCoolingDown,
    isResendDisabled,
    pendingEmail,
    resend,
    resendAriaLabel,
    secondsRemaining,
  } = usePendingEmailSurface();

  // Reset the session dismiss when the pending email clears so the banner
  // starts fresh for the next change cycle instead of staying hidden after
  // cancel → request-again within the same layout session.
  useEffect(() => {
    if (!pendingEmail) {
      toggleIsDismissed(false);
    }
  }, [pendingEmail, toggleIsDismissed]);

  if (!pendingEmail) {
    return null;
  }

  return (
    <>
      {!isDismissed && (
        // AlertBanner defaults to role="alert" (assertive), which would interrupt
        // screen readers on every page nav while a pending email exists. This
        // banner is a persistent informational notice, not a time-critical
        // alert — override to role="status" (polite) at this call site.
        <AlertBanner color="blue" data-testid="PENDING_EMAIL_BANNER" onDismiss={toggleIsDismissed} role="status">
          Please verify your new email address by clicking the link sent to {pendingEmail}.&nbsp;
          {/* Keep the action cluster together so Resend + countdown + "or" + Cancel never split across lines. */}
          <span className="whitespace-nowrap">
            <button
              aria-label={resendAriaLabel}
              className="rounded-2 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-9 disabled:opacity-60"
              disabled={isResendDisabled}
              onClick={resend}
              type="button"
            >
              Resend email
            </button>
            {isCoolingDown && <span aria-hidden="true">&nbsp;({secondsRemaining}s)</span>}
            &nbsp;or&nbsp;
            <button
              aria-label="Cancel email change"
              className="rounded-2 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-9 disabled:opacity-60"
              disabled={isCanceling}
              onClick={cancel}
              type="button"
            >
              Cancel
            </button>
          </span>
        </AlertBanner>
      )}
      {/* Live region stays mounted even when the banner itself is dismissed — some SR/browser combos (JAWS + Chrome) won't announce content injected into a polite region that just appeared in the DOM, so keep the element alive across state changes. */}
      <span aria-atomic="true" aria-live="polite" className="sr-only">
        {!isDismissed && isCoolingDown ? 'Verification email sent. Please wait before resending.' : ''}
      </span>
    </>
  );
});

PendingEmailBanner.displayName = 'PendingEmailBanner';
